import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseCategory, BasePetition, Category, Petition, PetitionState } from '@ncessor/shared';
import { Observable, of as observableOf, ReplaySubject } from 'rxjs';
import { catchError, concatMap, first, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { MessageService } from './message.service';

export type GetPetitionsOptions = {
  category?: string;
  state?: PetitionState;
  dateOverride?: string; // ISO date string; overrides the current date for filtering.
};

@Injectable({
  providedIn: 'root',
})
export class NcessorService {
  private readonly serverUrl: string;
  private readonly httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  };
  private readonly userId$ = new ReplaySubject<string>();
  private readonly dateOverride = environment.dateOverride;

  constructor(
    private auth: AuthService,
    private http: HttpClient,
    private messageService: MessageService,
  ) {
    const host = environment.api.ncessorUrl;

    this.serverUrl = `${host ? host : ''}/api`;
    this.auth.authUser$.subscribe((user) => {
      if (user) {
        this.userId$.next(user.uid);
      }
    });
  }

  getCategories(): Observable<Category[]> {
    return this.makeHttpServerCall(
      (uid) => this.http.get<Category[]>(this.getCatUrl(uid)),
      (result) => `Fetched categories: ${result.length} items.`,
      'getCategories',
      [],
    );
  }

  getCategoryById(id: string): Observable<Category | null> {
    if (id === 'undefined') {
      throw new Error(`Invalid category id: "undefined"`);
    }
    return this.makeHttpServerCall(
      (uid) => this.http.get<Category>(`${this.getCatUrl(uid)}/${id}`),
      (result) => `Fetched category: ${result.name}`,
      'getCategoryById',
    );
  }

  addCategory(category: BaseCategory): Observable<Category> {
    return this.makeHttpServerCall(
      (userId) => this.http.post<Category>(this.getCatUrl(userId), category, this.httpOptions),
      (result) => `Added category: ${result.name}`,
      'addCategory',
    );
  }

  updateCategory(category: Category): Observable<Category> {
    return this.makeHttpServerCall(
      (uid) =>
        this.http.put<Category>(
          `${this.getCatUrl(uid)}/${category._id}`,
          category,
          this.httpOptions,
        ),
      (result) => `Updated category: ${result._id}`,
      'updateCategory',
    );
  }

  changeCategoryOrder(catId: string, newOrder: number) {
    return this.makeHttpServerCall(
      (uid) =>
        this.http.put<Category>(
          `${this.getCatUrl(uid)}/${catId}/order/${newOrder}`,
          null,
          this.httpOptions,
        ),
      (result) => `Reordered category ${catId} to ${newOrder}`,
      'changeCategoryOrder',
    );
  }

  deleteCategory(catId: string) {
    return this.makeHttpServerCall(
      (userId) => this.http.delete<Category | null>(`${this.getCatUrl(userId)}/${catId}`),
      () => `Deleted category: ${catId}`,
      'deleteCategory',
    );
  }

  getPetitions(options: GetPetitionsOptions): Observable<Petition[]> {
    let newOptions = { ...options };
    if (this.dateOverride) {
      newOptions.dateOverride = this.dateOverride;
    }
    console.log(`getPetitions(${JSON.stringify(newOptions)})`);

    return this.makeHttpServerCall(
      (uid) => this.http.get<Petition[]>(this.getGetPetitionsUrl(uid, newOptions)),
      (result) => `Fetched petitions: ${result.length} items.`,
      'getPetitions',
      [],
    );
  }

  private getGetPetitionsUrl(userId: string, options: GetPetitionsOptions) {
    let optionsStr = '';
    if (options.category || options.state || options.dateOverride) {
      if (options.category) {
        optionsStr += `category=${options.category}`;
      }
      if (options.state) {
        if (optionsStr.length > 0) {
          optionsStr += '&';
        }
        optionsStr += `state=${options.state}`;
      }
      if (options.dateOverride) {
        if (optionsStr.length > 0) {
          optionsStr += '&';
        }
        optionsStr += `dateOverride=${options.dateOverride}`;
      }
    }
    let url = this.getPetUrl(userId);
    return optionsStr.length > 0 ? url + '?' + optionsStr : url;
  }

  getPetitionById(petId: string): Observable<Petition | null> {
    if (petId === 'undefined') {
      throw new Error(`Invalid category id: "undefined"`);
    }
    return this.makeHttpServerCall(
      (userId) => this.http.get<Petition>(`${this.getPetUrl(userId)}/${petId}`),
      (result) => `Fetched petition: ${result?._id}`,
      'getPetitionById',
      null,
    );
  }

  addPetition(petition: BasePetition): Observable<Petition> {
    return this.makeHttpServerCall(
      (userId) => this.http.post<Petition>(this.getPetUrl(userId), petition, this.httpOptions),
      (result) => `Added petition: ${result.text}`,
      'addPetition',
    );
  }

  updatePetition(petition: Petition): Observable<Petition> {
    const petId = petition._id;
    return this.makeHttpServerCall(
      (userId) =>
        this.http.put<Petition>(`${this.getPetUrl(userId)}/${petId}`, petition, this.httpOptions),
      (result) => `Updated petition: ${result._id}`,
      'updatePetition',
    );
  }

  deletePetition(petId: string) {
    return this.makeHttpServerCall(
      (userId) => this.http.delete<Petition | null>(`${this.getPetUrl(userId)}/${petId}`),
      () => `Deleted petition: ${petId}`,
      'deletePetition',
    );
  }

  getCatUrl(userId: string) {
    return `${this.serverUrl}/${userId}/category`;
  }

  getPetUrl(userId: string) {
    return `${this.serverUrl}/${userId}/petition`;
  }

  private makeHttpServerCall<T>(
    httpFunc: (userId: string) => Observable<T>,
    logSuccessFunc: (result: T) => string,
    operationName: string,
    nullResult?: T,
  ): Observable<T> {
    return this.callFuncWithUserId(httpFunc).pipe(
      tap((result) => {
        this.log(logSuccessFunc(result));
      }),
      catchError(this.handleError<T>(operationName, nullResult)),
    );
  }

  private callFuncWithUserId<T>(userIdFunc: (userId: string) => Observable<T>): Observable<T> {
    return this.userId$.pipe(
      first(),
      concatMap((userId) => userIdFunc(userId)),
    );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      const msg = error.message ? `with the message "${error.message}"` : '';

      console.log(`Error handler called ${msg}`);
      // TODO: send the error to remote logging infrastructure.
      console.error(JSON.stringify(error, null, 2));

      // TODO: better job of transforming error for user consumption.
      this.messageService.logWarning(`${operation}: Failed: ${msg}`);

      // Let the app keep running by returning an empty result.
      console.log(`Error handler: return observableOf( ${result})`);
      return observableOf(result as T);
    };
  }

  private log(message: string): void {
    this.messageService.logFine(`NcessorService: ${message}`);
  }
}
